import React, { useState, useEffect, useRef } from 'react';

import { API } from '../lib/ortam';

// Bracket-matching replace: \textbf{...} icinde $math$ veya ic ice {} varsa
// [^}]* regex'i ilk } gorunce keser ve icerigi kacirir. Bu yardimci tum metin
// uzerinden tarayarak dengelenmis } kapanisini bulur.
export function bracketReplace(text, prefix, wrapper) {
  let result = '';
  let i = 0;
  while (i < text.length) {
    const idx = text.indexOf(prefix, i);
    if (idx === -1) { result += text.slice(i); break; }
    result += text.slice(i, idx);
    let depth = 1;
    let j = idx + prefix.length;
    while (j < text.length && depth > 0) {
      if (text[j] === '{') depth++;
      else if (text[j] === '}') depth--;
      j++;
    }
    const inner = text.slice(idx + prefix.length, j - 1);
    result += wrapper(inner);
    i = j;
  }
  return result;
}

// Math bolgelerini atlayarak metin bolgelerini HTML'e donustur.
// 2026-05-15 fix: metin bolgelerinde < ve > HTML-escape ediliyor; aksi halde
// "a<b<c" gibi icerik innerHTML tarafindan HTML tag olarak yorumlanip kayboluyor.
// Math bolgelerinde "<" ve ">" KaTeX'in tanidigi \lt ve \gt komutlarina cevrilir
// (HTML-safe + KaTeX render gorsel olarak ayni < > olur).
// HTML tag donusumleri (\textbf -> <strong>) split'ten once placeholder ile
// maskelenir, boylece escape onlari bozmaz.
//
// DIKKAT — bu fonksiyon `SoruKarti.preprocessLatex` ile AYNI kalmali; iki kopya
// `LatexUtils.test.js`teki esdegerlik testiyle kilitli. Kopyalardan biri
// degisirse test kirilir (2026-08-17: buradaki \x01 sentinel sessizce dusmustu,
// SoruKarti korumaliydi ama bu dosyanin testi yoktu -> TestGorunum ekraninda
// metindeki HER sayi "undefined" basiyordu).
export function preprocessLatex(text) {
  const placeholders = [];
  // Sentinel (\x01) ZORUNLU: token'i ciplak sayi olarak yazarsan geri koyma
  // adimindaki regex metindeki GERCEK sayilari da token indeksi sanar ve
  // placeholders[N] === undefined ile ezer. Gorunmez karakter — silme.
  const ph = (html) => {
    placeholders.push(html);
    return `${placeholders.length - 1}`;
  };

  // 0. Isaret kacisi -- math/metin ayrimiyla. Math bolgesinde duz < > KaTeX'in
  // tanidigi \lt / \gt olur (innerHTML "$a<b$" icindeki "<b"yi etiket sanip
  // icerigi yemesin); metin bolgesinde & < > HTML varligina cevrilir. Satir
  // sonu (\n -> <br>) YALNIZ ust duzeyde (adim 3).
  // AYNI kacis \underline/\textit/\textbf/** GOVDESINE de uygulanir
  // (2026-09-15): eskiden govde split'ten ONCE placeholder'a alinip hicbir
  // kacistan gecmeden innerHTML'e gidiyordu; `\textbf{Buna gore $0<x<360$ ...}`
  // icindeki "<x<360$ ..." bir ETIKET aciyor, govdenin kalani siliniyordu
  // (canli #7170 "Buna gore $0"; SoruKarti kopyasiyla AYNI kalmali).
  const kacir = (s, satirSonu) => s.split(
    /(\$\$[\s\S]+?\$\$|\\\[[\s\S]+?\\\]|\$[^$]+?\$|\\\([\s\S]+?\\\))/
  ).map((part, i) => {
    if (i % 2 === 1) return part.replace(/</g, '\\lt ').replace(/>/g, '\\gt ');
    const metin = part.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    return satirSonu ? metin.replace(/\\n/g, '<br>').replace(/\n/g, '<br>') : metin;
  }).join('');

  // 1. LaTeX metin komutlarini HTML tag'e donustur (placeholder ile koru)
  // SIRA onemli (SoruKarti ile ayni): en ICTEKI adaylar once maskelenir.
  // \textbf ilk islenirse `\textbf{... \underline{x} ...}` icindeki \underline
  // placeholders[0]'in ICINE dusuyor ve bir daha taranmadigi icin HAM basiliyor.
  text = bracketReplace(text, '\\underline{', (s) => ph(`<u>${kacir(s, false)}</u>`));
  text = bracketReplace(text, '\\textit{',    (s) => ph(`<em>${kacir(s, false)}</em>`));
  text = bracketReplace(text, '\\textbf{',    (s) => ph(`<strong>${kacir(s, false)}</strong>`));

  // 2. Markdown bold geri uyumluluk: **...** -> <strong>
  text = text.replace(/\*\*([^*\n]+?)\*\*/g, (m, p1) => ph(`<strong>${kacir(p1, false)}</strong>`));

  // 3. Ust duzey: math bolgelerini ayir; metinde HTML-escape + newline
  let out = kacir(text, true);

  // 4. Placeholder'lari geri koy (HTML tag'ler escape'lenmis metnin arasina
  // eklenir; math bolgesi disindalar, KaTeX renderMathInElement DOM dolasir,
  // math'i <strong> icinde de bulup render eder)
  let _prev;
  do {
    _prev = out;
    out = out.replace(/(\d+)/g, (_, idx) => placeholders[parseInt(idx, 10)]);
  } while (out !== _prev && /\d+/.test(out));

  return out;
}

export function LatexText({ children }) {
  const ref = useRef(null);
  useEffect(() => {
    if (!ref.current) return;
    ref.current.innerHTML = preprocessLatex(children || '');
    if (window.renderMathInElement) {
      window.renderMathInElement(ref.current, {
        delimiters: [
          { left: '$$', right: '$$', display: true },
          { left: '$',  right: '$',  display: false },
          { left: '\\(', right: '\\)', display: false },
          { left: '\\[', right: '\\]', display: true },
        ],
        throwOnError: false,
      });
    }
  }, [children]);
  return <span ref={ref} />;
}

export function TikzDiagram({ tikzCode }) {
  const [svg, setSvg] = useState(null);
  const [hata, setHata] = useState(null);
  const [yukleniyor, setYukleniyor] = useState(false);
  const [hamGoster, setHamGoster] = useState(false);

  useEffect(() => {
    if (!tikzCode) return;
    let cancelled = false;
    setYukleniyor(true); setHata(null);
    fetch(`${API}/api/tikz/render`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ tikz: tikzCode }) })
      .then(r => r.json())
      .then(data => { if (!cancelled) { data.basarili ? setSvg(data.svg) : setHata(data.hata || 'Render basarisiz'); } })
      .catch(e => { if (!cancelled) setHata(e.message); })
      .finally(() => { if (!cancelled) setYukleniyor(false); });
    return () => { cancelled = true; };
  }, [tikzCode]);

  return (
    <div className="my-3">
      <div className="flex justify-end mb-1">
        <button onClick={() => setHamGoster(!hamGoster)} className="px-3 py-1 rounded border border-gray-300 bg-white text-xs cursor-pointer hover:bg-gray-50">
          {hamGoster ? 'Gorsel' : 'TikZ Kodu'}
        </button>
      </div>
      {hamGoster ? (
        <pre className="text-xs bg-gray-100 p-2 rounded overflow-auto font-mono">{tikzCode}</pre>
      ) : yukleniyor ? (
        <div className="text-center py-6 text-gray-500 font-serif">Diyagram derleniyor...</div>
      ) : hata ? (
        <div className="p-3">
          <div className="text-orange-600 mb-2">Diyagram derlenemedi: {hata}</div>
          <pre className="text-xs bg-gray-100 p-2 rounded overflow-auto font-mono">{tikzCode}</pre>
        </div>
      ) : svg ? (
        <div className="text-center py-2 overflow-auto max-w-full" dangerouslySetInnerHTML={{ __html: svg }} />
      ) : (
        <pre className="text-xs bg-gray-100 p-2 rounded overflow-auto font-mono">{tikzCode}</pre>
      )}
    </div>
  );
}
